import Box from '@mui/material/Box';
import ButtonBase, { ButtonBaseProps } from '@mui/material/ButtonBase';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { ALLIANCE_MOBILE_BREAKPOINT, APP_BAR } from './alliance-tokens';

/**
 * True below the md breakpoint (900px), where every Alliance app switches to
 * the phone layout: temporary drawers, avatar-only user control and bottom
 * sheets for the launcher and user menu.
 */
export function useIsMobileLayout() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down(ALLIANCE_MOBILE_BREAKPOINT));
}

/**
 * A 40px bordered control for the App Bar (menu, cart, launcher, user
 * control). The bar is always dark, so the border and hover are always the
 * dark-mode values.
 *
 * @param open - Shows the "open" fill while the control's menu is open
 * @param square - 40×40 icon button; otherwise it grows with its content
 */
export const BarButton = React.forwardRef<
  HTMLButtonElement,
  ButtonBaseProps & { open?: boolean; square?: boolean }
>(function BarButton(props, ref) {
  // Props
  const { open, square, sx, ...rest } = props;

  return (
    <ButtonBase
      ref={ref}
      {...rest}
      sx={[
        {
          fontFamily: 'inherit',
          height: APP_BAR.controlHeight,
          minWidth: APP_BAR.controlHeight,
          width: square ? APP_BAR.controlHeight : 'auto',
          flex: 'none',
          borderRadius: `${APP_BAR.controlRadius}px`,
          border: `1px solid ${APP_BAR.controlBorder}`,
          color: '#ffffff',
          position: 'relative',
          backgroundColor: open ? APP_BAR.controlOpen : 'transparent',
          transition: 'background-color 0.15s',
          '&:hover': {
            backgroundColor: open ? APP_BAR.controlOpen : APP_BAR.controlHover,
          },
          '&.Mui-focusVisible': {
            outline: '2px solid #ffffff',
            outlineOffset: 2,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
});

/** Initials for an avatar tile: first letter of the first two words */
export const getInitials = (params: { name: string }) =>
  params.name
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/**
 * The rounded-square initials tile used in the user control, the user menu
 * and the System Screen identity chip.
 */
export function AvatarTile(props: {
  initials: string;
  size: number;
  background: string;
  color: string;
  children?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        width: props.size,
        height: props.size,
        flex: 'none',
        borderRadius: props.size >= 40 ? '10px' : `${APP_BAR.avatarRadius}px`,
        backgroundColor: props.background,
        color: props.color,
        fontSize: props.size >= 40 ? 16 : 12,
        fontWeight: 700,
        letterSpacing: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {props.initials}
      {props.children}
    </Box>
  );
}
